import Env from 'src/environmentVariables/EnvironmentVariables';
import { HistoryType } from 'src/@types/historyTables';
import { Dayjs } from 'dayjs';
import { notify } from './notifications';
import { MarketFilter } from 'src/@types/types';
import { MAX_NUM_OF_MONTHS_FETCH_LONG_TERM } from 'src/constants/historyTables';
import { dlog } from '../dev';
import { Opaque } from '@drift/common';

/**
 * This client is used to fetch data from the Data API.
 *
 * You can see the swagger docs for the data API here : https://data.api.drift.trade/playground
 *
 * We have split the client into two types of requests:
 * 1. Short term data requests
 * 2. Long term data requests
 *
 * Short term requests are used to fetch "recent", in a paginated way back in time.
 * Long term requests are used to fetch data for a specific date range.
 *
 * For short term requests they will return a pageToken, which is a string, which you can use to fetch the next page of data.
 * - It's important to note that this page token can be considered an index into that point of data that WON'T CHANGE if new data comes in. E.g. if your fetch the most recent 20 records on an endpoint, and store the pageToken for the next 20 records, and then new data comes before you fetch the next 20 records - the result will still be the same, it will be the following 20 records after whatever the last record in your previous fetch was.
 * For long term requests they will return a list of records. Instead of a pageToken they are just paginated by a number.
 */

type DataApiResponse<T> = {
	success: boolean;
	records: T[];
	meta: {
		nextPage: null | string;
	};
};

export type ShortTermDataApiPathParams = {
	marketFilter?: MarketFilter;
	orderId?: number;
	userAccount?: string;
	authority?: string;
	marketSymbol?: string;
};

export type ShortTermDataApiQueryParams = {
	limit?: number;
	// pageToken?: string; // Not implemented :: Currently we treat pageToken as a seperate param to pass into the main function. It may be an improvement to generalize it into here
};

export type LongTermDataApiQueryParams = {
	// format?: 'json' | 'csv'; Not implemented :: this is available on /user/{accountId}/deposits/{year}/{month} for example
	// pageToken?: number; // Not implemented :: Currently we treat pageToken as a seperate param to pass into the main function. It may be an improvement to generalize it into here
};

const API_SECRET = process.env.NEXT_PUBLIC_INTERNAL_SECRET ?? '';

const USER_ACCOUNT_PLACEHOLDER = '{userAccount}'; // NOTE:: In the data API swagger docs this is labelled {accountId}
const AUTHORITY_ID_PLACEHOLDER = '{authorityId}';
const MARKET_FILTER_PLACEHOLDER = '{marketFilter}';
const MARKET_SYMBOL_PLACEHOLDER = '{marketSymbol}'; // NOTE:: In the data API swagger docs this is labelled {symbol}
const ORDER_ID_PLACEHOLDER = '{orderId}';
const YEAR_PLACEHOLDER = '{year}';
const MONTH_PLACEHOLDER = '{month}';
const REPLACEABLE_PLACEHOLDERS_TO_PARAMS = {
	[USER_ACCOUNT_PLACEHOLDER]: 'userAccount',
	[AUTHORITY_ID_PLACEHOLDER]: 'authority',
	[MARKET_FILTER_PLACEHOLDER]: 'marketFilter',
	[MARKET_SYMBOL_PLACEHOLDER]: 'marketSymbol',
	[ORDER_ID_PLACEHOLDER]: 'orderId',
	[YEAR_PLACEHOLDER]: 'year',
	[MONTH_PLACEHOLDER]: 'month',
};
const REPLACEABLE_PLACEHOLDERS = Object.keys(
	REPLACEABLE_PLACEHOLDERS_TO_PARAMS
);

export type ShortTermDataApiLookupTypes =
	| HistoryType
	| 'recentMarketTrades'
	| 'predictionMarketTrades';

const SHORT_TERM_DATA_API_LOOKUP: Partial<
	Record<ShortTermDataApiLookupTypes, string>
> = {
	bal: `/user/${USER_ACCOUNT_PLACEHOLDER}/lp`,
	deposits: `/user/${USER_ACCOUNT_PLACEHOLDER}/deposits`,
	predictionTrades: `/user/${USER_ACCOUNT_PLACEHOLDER}/predictions`,
	predictionMarketTrades: `/market/${MARKET_SYMBOL_PLACEHOLDER}/predictions`,
	swaps: `/user/${USER_ACCOUNT_PLACEHOLDER}/swaps`,
	'funding-payments': `/user/${USER_ACCOUNT_PLACEHOLDER}/fundingPayments`,
	'if-staking': `/authority/${AUTHORITY_ID_PLACEHOLDER}/insuranceFundStake`,
	liquidations: `/user/${USER_ACCOUNT_PLACEHOLDER}/liquidations`,
	'settled-balances': `/user/${USER_ACCOUNT_PLACEHOLDER}/settlePnl`,
	trades: `/user/${USER_ACCOUNT_PLACEHOLDER}/trades`,
	recentMarketTrades: `/market/${MARKET_SYMBOL_PLACEHOLDER}/trades`,
	orders: `/user/${USER_ACCOUNT_PLACEHOLDER}/orders/${MARKET_FILTER_PLACEHOLDER}`,
	orderActions: `/user/${USER_ACCOUNT_PLACEHOLDER}/orders/${ORDER_ID_PLACEHOLDER}/actions`,
	rewards: `/user/${USER_ACCOUNT_PLACEHOLDER}/rewards`,
};

const LONG_TERM_DATA_API_LOOKUP: Partial<Record<HistoryType, string>> = {
	bal: `/user/${USER_ACCOUNT_PLACEHOLDER}/lp/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	deposits: `/user/${USER_ACCOUNT_PLACEHOLDER}/deposits/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	predictionTrades: `/user/${USER_ACCOUNT_PLACEHOLDER}/predictions/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	swaps: `/user/${USER_ACCOUNT_PLACEHOLDER}/swaps/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'funding-payments': `/user/${USER_ACCOUNT_PLACEHOLDER}/fundingPayments/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'if-staking': `/authority/${AUTHORITY_ID_PLACEHOLDER}/insuranceFundStake/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	liquidations: `/user/${USER_ACCOUNT_PLACEHOLDER}/liquidations/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'settled-balances': `/user/${USER_ACCOUNT_PLACEHOLDER}/settlePnl/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	trades: `/user/${USER_ACCOUNT_PLACEHOLDER}/trades/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	rewards: `/user/${USER_ACCOUNT_PLACEHOLDER}/rewards/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
};

const getShortTermDataApiRouteForHistoryPage = (
	historyType: ShortTermDataApiLookupTypes,
	params?: ShortTermDataApiPathParams
) => {
	const route = SHORT_TERM_DATA_API_LOOKUP[historyType];
	if (!route) {
		throw new Error(`History type ${historyType} not found`);
	}

	const placeholdersToReplace = REPLACEABLE_PLACEHOLDERS.filter(
		(placeholder) => {
			return route.includes(placeholder);
		}
	);

	const routeWithPlaceholdersReplaced = placeholdersToReplace.reduce(
		(acc, placeholder) => {
			const arg =
				params[
					REPLACEABLE_PLACEHOLDERS_TO_PARAMS[
						placeholder as keyof typeof REPLACEABLE_PLACEHOLDERS_TO_PARAMS
					] as keyof typeof params
				];

			if (arg === undefined || arg === null) {
				throw new Error(`Argument for placeholder '${placeholder}' not found`);
			}

			return acc.replace(placeholder, arg.toString());
		},
		route
	);

	return routeWithPlaceholdersReplaced;
};

const getLongTermDataApiRouteForHistoryPage = (
	historyType: HistoryType,
	params?: {
		userAccount?: string;
		authority?: string;
		month?: number;
		year?: number;
	}
) => {
	const route = LONG_TERM_DATA_API_LOOKUP[historyType];

	if (!route) {
		throw new Error(`History type ${historyType} not found`);
	}

	const placeholdersToReplace = REPLACEABLE_PLACEHOLDERS.filter(
		(placeholder) => {
			return route.includes(placeholder);
		}
	);

	const routeWithPlaceholdersReplaced = placeholdersToReplace.reduce(
		(acc, placeholder) => {
			const arg =
				params[
					REPLACEABLE_PLACEHOLDERS_TO_PARAMS[
						placeholder as keyof typeof REPLACEABLE_PLACEHOLDERS_TO_PARAMS
					] as keyof typeof params
				];

			if (arg === undefined || arg === null) {
				throw new Error(`Argument for placeholder '${placeholder}' not found`);
			}

			if (placeholder === MONTH_PLACEHOLDER) {
				return acc.replace(placeholder, `${params?.month + 1}`);
			}

			return acc.replace(placeholder, arg.toString());
		},
		route
	);

	return routeWithPlaceholdersReplaced;
};

/**
 * A string representing URL query parameters in the format "param1=value1&param2=value2"
 * This branded type ensures we're working with properly formatted query params
 */
type QueryParamsString = Opaque<string, 'QueryParamsString'>;

const urlSearchParamsToQueryParamsString = (
	searchParams: URLSearchParams
): QueryParamsString => {
	const paramsString = searchParams.toString();
	return (paramsString ? `?${paramsString}` : '') as QueryParamsString; // URLSearchParams.toString() returns a string of the form "param1=value1&param2=value2" so we need to add a ? to the start of it
};

const buildShortTermDataApiQueryParams = (
	pageToken?: string,
	queryParams?: ShortTermDataApiQueryParams
): QueryParamsString => {
	const queryParamsAccumulator = new URLSearchParams();

	if (pageToken) {
		queryParamsAccumulator.set('page', pageToken);
	}

	if (queryParams) {
		// Process each property of ShortTermDataApiQueryParams
		// This approach maintains exhaustive type checking
		const processQueryParam = (key: keyof ShortTermDataApiQueryParams) => {
			switch (key) {
				case 'limit':
					if (queryParams.limit !== undefined) {
						queryParamsAccumulator.set('limit', queryParams.limit.toString());
					}
					break;
				default: {
					// This will cause a TypeScript error if we haven't handled all possible query params
					const _exhaustiveCheck: never = key;
					break; // NOTE :: We are strict typechecking here so that we don't MISS any params - but the params that get included in `queryParams` may include the other path params (because we allow them to be passed together in a single object) -- so we DON'T want to error here.
				}
			}
		};

		// Process each key in the queryParams object
		(
			Object.keys(queryParams) as Array<keyof ShortTermDataApiQueryParams>
		).forEach(processQueryParam);
	}

	return urlSearchParamsToQueryParamsString(queryParamsAccumulator);
};

const buildLongTermDataApiQueryParams = (
	pageToken?: string
): QueryParamsString => {
	const queryParamsAccumulator = new URLSearchParams();

	if (pageToken) {
		queryParamsAccumulator.set('page', pageToken);
	}

	return urlSearchParamsToQueryParamsString(queryParamsAccumulator);
};

const createDataApiFullUrl = (path: string, queryParams: string) => {
	return `${Env.dataApiUrl}${path}${queryParams}`;
};

class DataApiClient {
	private static readonly MAX_LONG_TERM_PAGINATION_DEPTH = 1; // prevent continuous fetching for accounts with a lot of data

	private static getFromDataApi<T>(
		path: string,
		queryParams: QueryParamsString
	): Promise<DataApiResponse<T>> {
		const fullUrl = createDataApiFullUrl(path, queryParams);

		return new Promise<DataApiResponse<T>>((res) => {
			const headers = new Headers();

			if (API_SECRET) {
				headers.append('Authorization', API_SECRET);
			}

			fetch(`${fullUrl}`, {
				headers,
			})
				.then(async (response) => {
					if (!response.ok) {
						res({
							success: false,
							records: [],
							meta: {
								nextPage: null,
							},
						});
						return;
					}

					const body = await response.json();

					res(body);
				})
				.catch(() => {
					res({ success: false, records: [], meta: { nextPage: null } });
				});
		});
	}

	public static async fetchShortTermData<T>(
		historyType: ShortTermDataApiLookupTypes,
		pageToken?: string,
		params?: ShortTermDataApiPathParams & ShortTermDataApiQueryParams
	) {
		const route = getShortTermDataApiRouteForHistoryPage(historyType, params);

		dlog(`history_tables_v2`, `fetching_short_term_data`, {
			route,
			pageToken,
		});

		const queryParamsString = buildShortTermDataApiQueryParams(
			pageToken,
			params
		);

		const result = await this.getFromDataApi<T>(route, queryParamsString);

		return result;
	}

	/**
	 * Fetch the necessary data for each month in the given date range. e.g. if the date range is 3rd June to 8th August, we fetch data for June, July and August.
	 * Fetches up to `MAX_NUM_OF_MONTHS_TO_FETCH` months of data, with up to `MAX_LONG_TERM_PAGINATION_DEPTH` pages for each month.
	 * Note that there may be missing data in between, e.g if month 1 has 10 pages, and month 2 has 1 page, only the first `MAX_LONG_TERM_PAGINATION_DEPTH` pages of month 1 will be fetched, before combining the data from month 2
	 * There is no concept of pagination for how we handle long term data, even though there is pagination on each data API. To keep things simple and efficient, since the data fetched is up to 5000 records, we think its sufficient
	 * for most users when we limit by `MAX_LONG_TERM_PAGINATION_DEPTH` of pages per month.
	 */
	public static async fetchLongTermData<T>(
		historyType: HistoryType,
		params?: {
			userAccount?: string;
			authority?: string;
			startDate?: Dayjs;
			endDate?: Dayjs;
		}
	) {
		const monthsWithinPeriod = this.getMonthsToFetch(
			params.startDate,
			params.endDate
		).sort((a, b) => b.getTime() - a.getTime()); // descending order
		const monthsToFetch = monthsWithinPeriod.slice(
			0,
			MAX_NUM_OF_MONTHS_FETCH_LONG_TERM
		);

		const fetchPage = async (
			route: string,
			records: T[],
			currentPageToken?: string | null,
			depth = 0
		): Promise<DataApiResponse<T>> => {
			if (depth >= this.MAX_LONG_TERM_PAGINATION_DEPTH || !currentPageToken) {
				if (!currentPageToken) {
					notify({
						message:
							'There is too much data to fetch, please narrow down your date range',
						type: 'warning',
					});
				}
				return {
					success: true,
					records,
					meta: { nextPage: null },
				};
			}

			try {
				const queryParamsString =
					buildLongTermDataApiQueryParams(currentPageToken);
				const response = await this.getFromDataApi<T>(route, queryParamsString);
				const updatedRecords = [...records, ...response.records];

				if (response.meta.nextPage) {
					return fetchPage(
						route,
						updatedRecords,
						response.meta.nextPage,
						depth + 1
					);
				}

				return {
					success: response.success,
					records: updatedRecords,
					meta: { nextPage: null },
				};
			} catch (err) {
				console.error(err);
				return {
					success: false,
					records: records,
					meta: { nextPage: currentPageToken },
				};
			}
		};

		dlog(`history_tables_v2`, `fetching_long_term_data`, {
			monthsToFetch,
			params,
		});

		const promises = monthsToFetch.map((monthDate) => {
			const month = monthDate.getMonth();
			const year = monthDate.getFullYear();

			const route = getLongTermDataApiRouteForHistoryPage(historyType, {
				userAccount: params.userAccount,
				authority: params.authority,
				month,
				year,
			});

			return fetchPage(route, [], '0');
		});

		const monthlyData = await Promise.all(promises);

		const allRecords = monthlyData.flatMap((data) => data.records);

		return {
			success: monthlyData.every((data) => data.success),
			records: allRecords,
		};
	}

	private static getMonthsToFetch(startDate: Dayjs, endDate: Dayjs) {
		const months: Date[] = [];
		let currentDate = startDate.clone().startOf('month');
		const endDateMonth = endDate.clone().startOf('month');

		while (currentDate.isSameOrBefore(endDateMonth)) {
			months.push(new Date(currentDate.year(), currentDate.month(), 1));
			currentDate = currentDate.add(1, 'month');
		}

		return months;
	}
}

export default DataApiClient;
